import * as R from "remeda";
import type { ConditionFieldDescription } from "@smartsuite/automation-engine-api";
import { ConditionFunction } from "@smartsuite/automation-engine-api";
import type {
  ActionCandidate,
  TriggerCandidate,
} from "../../../../../../../types/candidate";
import {
  allItemsResolvedFn,
  anyFn,
  changesFn,
  containsFn,
  greaterThanFn,
  greaterThanOrEqualsFn,
  hasAllOfFn,
  hasAnyOfFn,
  hasNoneOfFn,
  isEmptyFn,
  isEqualsFn,
  isNotEmptyFn,
  isNotEqualsFn,
  lessThanFn,
  lessThanOrEqualsFn,
  notAllItemsResolvedFn,
  notContainsFn,
  percentCompleteGreaterThanFn,
  percentCompleteLessThanFn,
  isCompleteFn,
  isNotCompleteFn,
  isAfterFn,
  isOnOrAfterFn,
  isOnOrBeforeFn,
  isFn,
  isNotFn,
} from "./functions";
import type { EditorId, FnConfig, NestedFnConfig } from "./types";
import { RECORD_CREATED_TRIGGER_IDS } from "../../../../../../../utils/constants";

const TRIGGERS_WITHOUT_CHANGES_FN = RECORD_CREATED_TRIGGER_IDS.filter(
  (triggerId) => triggerId !== "updated-record",
);

type Presentation = (
  editorId: EditorId,
  multiple: boolean,
  smartsuiteFieldType?: string,
) => FnConfig;

/**
 * How each operator the contract defines is rendered: its label, whether its
 * value input takes several values, and which value editor it uses. Which
 * operators a field offers is the integration's to state, in
 * `supportedFunctions`; this table only says how to draw one.
 *
 * The date and linked-record branches pick between existing translations of the
 * same operator ("Is after" for a date, "Greater than" otherwise) -- wording,
 * not availability. An operator absent from the table has no translation yet
 * and cannot be drawn at all.
 */
const PRESENTATION: Partial<Record<ConditionFunction, Presentation>> = {
  [ConditionFunction.IS_EQUALS]: (editorId, multiple) =>
    editorId === "date"
      ? isFn(multiple, editorId)
      : isEqualsFn(multiple, editorId),
  [ConditionFunction.IS_NOT_EQUALS]: (editorId, multiple) =>
    editorId === "date"
      ? isNotFn(multiple, editorId)
      : isNotEqualsFn(multiple, editorId),
  [ConditionFunction.IS_EMPTY]: () => isEmptyFn(),
  [ConditionFunction.IS_NOT_EMPTY]: () => isNotEmptyFn(),
  [ConditionFunction.IS_ANY]: () => anyFn(),
  [ConditionFunction.CHANGES]: () => changesFn(),
  [ConditionFunction.HAS_ANY_OF]: (editorId) => hasAnyOfFn(editorId),
  [ConditionFunction.HAS_ALL_OF]: (editorId) => hasAllOfFn(editorId),
  [ConditionFunction.HAS_NONE_OF]: (editorId) => hasNoneOfFn(editorId),
  [ConditionFunction.GREATER_THAN]: (editorId) =>
    editorId === "date" ? isAfterFn(editorId) : greaterThanFn(editorId),
  [ConditionFunction.GREATER_THAN_OR_EQUALS]: (editorId) =>
    editorId === "date"
      ? isOnOrAfterFn(editorId)
      : greaterThanOrEqualsFn(editorId),
  [ConditionFunction.LESS_THAN]: (editorId) => lessThanFn(editorId),
  [ConditionFunction.LESS_THAN_OR_EQUALS]: (editorId) =>
    editorId === "date"
      ? isOnOrBeforeFn(editorId)
      : lessThanOrEqualsFn(editorId),
  [ConditionFunction.CONTAINS]: (editorId, _multiple, smartsuiteFieldType) =>
    containsFn(editorId, smartsuiteFieldType),
  [ConditionFunction.NOT_CONTAINS]: (
    editorId,
    _multiple,
    smartsuiteFieldType,
  ) => notContainsFn(editorId, smartsuiteFieldType),
  [ConditionFunction.ALL_ITEMS_RESOLVED]: () => allItemsResolvedFn(),
  [ConditionFunction.NOT_ALL_ITEMS_RESOLVED]: () => notAllItemsResolvedFn(),
  [ConditionFunction.PERCENT_COMPLETE_GREATER_THAN]: () =>
    percentCompleteGreaterThanFn("number"),
  [ConditionFunction.PERCENT_COMPLETE_LESS_THAN]: () =>
    percentCompleteLessThanFn("number"),
  [ConditionFunction.IS_COMPLETE]: () => isCompleteFn(),
  [ConditionFunction.IS_NOT_COMPLETE]: () => isNotCompleteFn(),
};

/** Config kind of an FnConfig back to the contract's enum, so an operator a
 * condition already holds can be looked up in PRESENTATION. Mirrors the engine's
 * own map in unsupported-condition-functions.ts. */
const CONDITION_FUNCTIONS: Record<string, ConditionFunction> = {
  isEquals: ConditionFunction.IS_EQUALS,
  isNotEquals: ConditionFunction.IS_NOT_EQUALS,
  isAnyOf: ConditionFunction.IS_ANY_OF,
  isNoneOf: ConditionFunction.IS_NONE_OF,
  isEmpty: ConditionFunction.IS_EMPTY,
  isNotEmpty: ConditionFunction.IS_NOT_EMPTY,
  isAny: ConditionFunction.IS_ANY,
  hasAnyOf: ConditionFunction.HAS_ANY_OF,
  hasAllOf: ConditionFunction.HAS_ALL_OF,
  hasNoneOf: ConditionFunction.HAS_NONE_OF,
  greaterThan: ConditionFunction.GREATER_THAN,
  greaterThanOrEquals: ConditionFunction.GREATER_THAN_OR_EQUALS,
  lessThan: ConditionFunction.LESS_THAN,
  lessThanOrEquals: ConditionFunction.LESS_THAN_OR_EQUALS,
  between: ConditionFunction.BETWEEN,
  betweenOrEquals: ConditionFunction.BETWEEN_OR_EQUALS,
  contains: ConditionFunction.CONTAINS,
  notContains: ConditionFunction.NOT_CONTAINS,
  changes: ConditionFunction.CHANGES,
  allItemsResolved: ConditionFunction.ALL_ITEMS_RESOLVED,
  notAllItemsResolved: ConditionFunction.NOT_ALL_ITEMS_RESOLVED,
  percentCompleteGreaterThan: ConditionFunction.PERCENT_COMPLETE_GREATER_THAN,
  percentCompleteLessThan: ConditionFunction.PERCENT_COMPLETE_LESS_THAN,
  isComplete: ConditionFunction.IS_COMPLETE,
  isNotComplete: ConditionFunction.IS_NOT_COMPLETE,
};

/** "Changes" and "Any" depend on where the condition sits -- which trigger, and
 * how deeply nested -- which the field cannot know, so the descriptions list
 * them wherever they could apply and this decides when they do. */
const applies = (
  fn: ConditionFunction,
  nestedFnConfig: NestedFnConfig,
): boolean => {
  switch (fn) {
    case ConditionFunction.CHANGES:
      return nestedFnConfig.doesChangesFnNeed;
    case ConditionFunction.IS_ANY:
      return nestedFnConfig.doesAnyFnNeed;
    default:
      return true;
  }
};

/**
 * The operators offered for a field, in the order its description lists them.
 *
 * Availability belongs to the integration that owns the field: it states the
 * operators in `supportedFunctions` and the editor renders those, rather than
 * inferring them from the editor kind and field type and disagreeing with the
 * engine (STR-14903). A field describing none therefore offers none.
 *
 * `selectedFunction` -- the operator the condition already holds -- is always
 * included, so one stored before the field described its operators stays
 * readable and can be changed rather than leaving a blank dropdown.
 */
export const getAvailableFunctions = (
  nestedFnConfig: NestedFnConfig,
  fieldDescription?: ConditionFieldDescription,
  selectedFunction?: string,
): FnConfig[] => {
  const editorId = fieldDescription?.editor?.editor.oneofKind;
  const functions = (fieldDescription?.supportedFunctions ?? []).filter((fn) =>
    applies(fn, nestedFnConfig),
  );
  const selected =
    selectedFunction === undefined
      ? undefined
      : CONDITION_FUNCTIONS[selectedFunction];
  if (selected !== undefined && !functions.includes(selected)) {
    functions.push(selected);
  }

  return functions
    .map((fn) =>
      PRESENTATION[fn]?.(
        editorId,
        // Equality against free text compares a list of accepted values; every
        // other editor compares one.
        editorId === "text",
        fieldDescription?.smartsuiteFieldType,
      ),
    )
    .filter((config): config is FnConfig => config !== undefined);
};

export const getFunctionConfig = (
  functionConfigs: FnConfig[],
  fnName?: string,
): FnConfig | undefined => functionConfigs.find((c) => c[0] === fnName);

export const getNestedFnConfig = (
  trigger?: TriggerCandidate,
  isTopLevelFn?: boolean,
  action?: ActionCandidate,
  excludeChangesFn?: boolean,
): NestedFnConfig => {
  if (
    action !== undefined ||
    R.isTruthy(excludeChangesFn) ||
    (trigger?.triggerReference?.triggerId !== undefined &&
      TRIGGERS_WITHOUT_CHANGES_FN.includes(trigger.triggerReference.triggerId))
  ) {
    return { doesChangesFnNeed: false, doesAnyFnNeed: false };
  } else {
    if (R.isTruthy(isTopLevelFn)) {
      return { doesChangesFnNeed: true, doesAnyFnNeed: false };
    } else {
      return { doesChangesFnNeed: false, doesAnyFnNeed: true };
    }
  }
};
